import { useEffect, useState } from "react";
import { personInitials } from "@/lib/personDisplay";
import ResponsiveImage from "@/components/ResponsiveImage";

/**
 * A circular portrait with an initials fallback, for admin lists.
 *
 * The fallback is not decorative. Only `speaker-portraits/` is publicly
 * servable, so a profile row still pointing at an intermediate prefix — a raw
 * `generated/` PNG left by an earlier version of the publication pipeline — now
 * returns 404. Without an error fallback those rows would render the browser's
 * broken-image glyph in a list of otherwise fine faces, which reads as a bug in
 * the page rather than as one profile needing its portrait re-published.
 *
 * `accent` ties the ring and the fallback to whatever colour the surrounding row
 * already uses for status, so the avatar does not introduce a second colour
 * system into a view that has one.
 */

/**
 * How large a face is in an admin table row — members, speakers, anywhere else.
 *
 * One constant rather than a number typed into each table, because the size was
 * previously written out at every call site and the tables drifted apart the
 * first time one of them was adjusted. Change this and every roster changes
 * together; that is the whole point of it existing.
 */
export const CRM_ROW_PORTRAIT_SIZE = 46;

export default function PersonPortraitAvatar({
  name,
  portraitUrl,
  portraitUrls,
  size = CRM_ROW_PORTRAIT_SIZE,
  accent = "#00D4FF",
}: {
  name: string;
  portraitUrl?: string | null;
  /**
   * Ordered portrait candidates. If one cannot be loaded, the next is tried
   * before the component falls back to initials.
   */
  portraitUrls?: Array<string | null | undefined>;
  size?: number;
  accent?: string;
}) {
  const candidates = Array.from(new Set(
    (portraitUrls ?? [portraitUrl])
      .map(value => value?.trim())
      .filter((value): value is string => Boolean(value)),
  ));
  const candidateKey = candidates.join("\n");
  const [candidateIndex, setCandidateIndex] = useState(0);

  // A row can be re-used for a different person as a list re-sorts or filters,
  // so a previous failure must not suppress the new person's portrait.
  useEffect(() => {
    setCandidateIndex(0);
  }, [candidateKey]);

  const activePortraitUrl = candidates[candidateIndex];

  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        borderRadius: "50%",
        border: `1px solid ${accent}59`,
        background: `${accent}18`,
        color: accent,
      }}
    >
      {activePortraitUrl ? (
        <ResponsiveImage
          src={activePortraitUrl}
          alt={`${name} portrait`}
          maxWidth={96}
          sizes={`${size}px`}
          loading="eager"
          onError={() => setCandidateIndex(index => index + 1)}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
            // Portraits are head-and-shoulders renders; anchoring to the top
            // keeps the face centred when a square crop trims the frame.
            objectPosition: "center top",
          }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: Math.max(10, Math.round(size * 0.34)),
            fontWeight: 700,
            letterSpacing: "-.04em",
            lineHeight: 1,
          }}
        >
          {personInitials(name)}
        </span>
      )}
    </div>
  );
}

/**
 * Member photos imported with the former Manus project.
 *
 * These are application assets rather than storage URLs, so they continue to
 * work after the old storage host is retired. The lookup is intentionally
 * conservative: only an exact normalized member name can claim an image.
 */
const IMPORTED_MEMBER_PHOTO_BY_NAME: Readonly<Record<string, string>> = {
  "aakriti maharjan": "/media/members/aakritimaharjan.jpeg",
  "monyca brown": "/media/members/monyca-brown.jpeg",
};

export function importedMemberPhotoUrl(name: string): string | null {
  return IMPORTED_MEMBER_PHOTO_BY_NAME[name.trim().toLocaleLowerCase()] ?? null;
}

/**
 * The shared profile-photo component for a member anywhere in the application.
 *
 * An approved published speaker portrait is the global display default. The
 * original member-owned photo remains the next candidate so source management,
 * unpublishing, and failed-image recovery never destroy the member upload.
 * PersonPortraitAvatar provides the final initials fallback.
 */
export function MemberProfilePhoto({
  name,
  memberPhotoUrl,
  speakerPortraitUrl,
  size = CRM_ROW_PORTRAIT_SIZE,
  accent = "#E91E8C",
}: {
  name: string;
  memberPhotoUrl?: string | null;
  speakerPortraitUrl?: string | null;
  size?: number;
  accent?: string;
}) {
  return (
    <PersonPortraitAvatar
      name={name}
      portraitUrls={[
        speakerPortraitUrl,
        memberPhotoUrl,
      ]}
      size={size}
      accent={accent}
    />
  );
}
